import { useCallback, useLayoutEffect } from "react";
import * as Sentry from "@sentry/react";
import { useSSOLogout } from "../../modules/Auth/api/apiHooks";
import { shallowEqual } from "react-redux";
import { useSelector } from "react-redux";
import { ApplicationState } from "../reducers";
import config from "../config";
import { handleSSOErrorByStatus } from "../hooks/useApiRequest";

const SSO_WIDGET_SCRIPT_ID = "sso-widget-script";
const SSO_WIDGET_MAX_RETRIES = 3;
const SSO_WIDGET_RETRY_DELAY_MS = 2000;
const SSO_WIDGET_INIT_WAIT_MS = 5000;
const SSO_WIDGET_INIT_POLL_INTERVAL_MS = 100;

// initWidget is registered by the widget's own script asynchronously after
// it loads, so it may not exist yet at script.onload time - poll for it
// instead of checking once, otherwise the widget silently never inits.
const waitForInitWidget = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (typeof window.initWidget === "function") {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      if (typeof window.initWidget === "function") {
        clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= SSO_WIDGET_INIT_WAIT_MS) {
        clearInterval(intervalId);
        reject(new Error("SSO Widget: initWidget was not registered in time"));
      }
    }, SSO_WIDGET_INIT_POLL_INTERVAL_MS);
  });

const injectSSOWidgetScript = (settings: SSOWidgetSettings) =>
  new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${config.SSO_WIDGET_URL}/index.js?timestamp=${Date.now()}`;
    script.async = true;
    script.id = SSO_WIDGET_SCRIPT_ID;
    script.onload = () => {
      waitForInitWidget()
        .then(() => {
          window.initWidget!(settings);
          resolve();
        })
        .catch((err) => {
          script.remove();
          reject(err);
        });
    };
    script.onerror = () => {
      script.remove();
      reject(new Error("SSO Widget: Failed to load the script"));
    };
    document.head.appendChild(script);
  });

const loadSSOWidget = async (
  settings: SSOWidgetSettings,
  retriesLeft = SSO_WIDGET_MAX_RETRIES
): Promise<void> => {
  if (document.getElementById(SSO_WIDGET_SCRIPT_ID)) return;

  try {
    await injectSSOWidgetScript(settings);
  } catch (err) {
    if (retriesLeft > 0) {
      await new Promise((r) => setTimeout(r, SSO_WIDGET_RETRY_DELAY_MS));
      return loadSSOWidget(settings, retriesLeft - 1);
    }

    Sentry.captureException(err);
  }
};

export const useSSOWidget = (cfg: Partial<SSOWidgetSettings>) => {
  const { rootSelector, announcementsRootSelector } = cfg || {};
  const { mutate: onLogout } = useSSOLogout();
  const token = useSelector(
    (state: ApplicationState) => state.auth.sso_token,
    shallowEqual
  );

  const onError = useCallback((err) => {
    handleSSOErrorByStatus(err.status);
  }, []);

  useLayoutEffect(() => {
    if (token) {
      loadSSOWidget({
        rootSelector,
        announcementsRootSelector,
        token,
        onLogout,
        onError,
        baseApiUrl: config.SSO_BASE_API_URL,
      });
    }
  }, [token, onLogout, onError, rootSelector, announcementsRootSelector]);
};
